import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { featureFlagsEndpoints } from '../api/featureFlagsEndpoints';

interface FeatureFlagsContextType {
  enabledFeatures: Set<string>;
  isLoading: boolean;
  error: Error | null;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const [enabledFeatures, setEnabledFeatures] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        setIsLoading(true);
        const response = await featureFlagsEndpoints.getEnabledFeatures();
        // Convert all feature names to lowercase for case-insensitive lookups
        setEnabledFeatures(new Set(response.enabled_features.map(f => f.toLowerCase())));
        setError(null);
      } catch (err) {
        console.error('Failed to fetch feature flags:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch feature flags'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeatureFlags();
  }, []);

  return (
    <FeatureFlagsContext.Provider value={{ enabledFeatures, isLoading, error }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlagsContext = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlagsContext must be used within a FeatureFlagsProvider');
  }
  return context;
};

/**
 * Hook to check if a specific feature is enabled
 * @param featureName - The name of the feature to check (case-insensitive)
 * @returns boolean - true if the feature is enabled, false otherwise
 */
export const useFeature = (featureName: string): boolean => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    return false;
  }
  return context.enabledFeatures.has(featureName.toLowerCase());
};
